// CoreUI Pro source reference: /Users/guochunzhong/git/oss/coreui-pro/js/src/stepper.js
import BaseComponent from "./base_component"
let generatedId = 0

export default class Stepper extends BaseComponent {
  static componentName = "stepper"
  static defaults = { linear: true, skipValidation: false }

  constructor(element, config = {}) {
    super(element, config)
    this.buttons = [...element.querySelectorAll(".stepper-step-button")]
    this.active = this.buttons.find((button) => button.classList.contains("active")) || this.buttons[0]
    this.initial = this.active
    this.setup()
    this.listen(element, "click", (event) => this.handleClick(event))
    this.listen(element, "keydown", (event) => this.handleKeydown(event))
  }

  setup() {
    this.buttons.forEach((button, index) => {
      if (!button.id) button.id = `stepper-step-${++generatedId}`
      button.setAttribute("role", "tab")
      button.setAttribute("aria-selected", button === this.active)
      button.tabIndex = button === this.active ? 0 : -1
      button.classList.toggle("active", button === this.active)
      button.classList.toggle("complete", index < this.buttons.indexOf(this.active))
      if (this.config.linear) button.disabled = index > this.buttons.indexOf(this.active) + 1
      const pane = this.pane(button)
      pane?.setAttribute("aria-hidden", button !== this.active)
      pane?.classList.toggle("active", button === this.active)
      pane?.classList.toggle("show", button === this.active)
      const content = button.parentElement.querySelector(":scope > .stepper-step-content")
      content?.classList.toggle("show", button === this.active)
    })
  }

  pane(button) {
    const selector = button?.dataset.coreuiTarget
    return selector ? this.element.querySelector(selector) : null
  }

  valid(button = this.active) {
    if (this.config.skipValidation) return true
    const target = this.pane(button) || button?.parentElement.querySelector(":scope > .stepper-step-content")
    const form = target?.querySelector("form")
    if (!form || form.checkValidity()) return true
    form.classList.add("was-validated")
    form.reportValidity()
    return false
  }

  showStep(button) {
    if (!button || button === this.active) return
    const currentIndex = this.buttons.indexOf(this.active)
    const targetIndex = this.buttons.indexOf(button)
    if (this.config.linear && targetIndex > currentIndex + 1) return
    if (targetIndex > currentIndex && !this.valid()) return
    this.active = button
    this.setup()
    button.focus({ preventScroll: true })
    this.emit("stepChange", { index: targetIndex + 1 })
  }

  next() {
    this.showStep(this.buttons[this.buttons.indexOf(this.active) + 1])
  }
  prev() {
    this.showStep(this.buttons[this.buttons.indexOf(this.active) - 1])
  }

  finish() {
    if (!this.valid() || this.active !== this.buttons.at(-1)) return this.next()
    this.finished = true
    this.active.classList.remove("active")
    this.active.classList.add("complete")
    this.buttons.forEach((button) => {
      button.disabled = true
    })
    this.pane(this.active)?.classList.remove("active", "show")
    this.emit("finish")
  }

  reset() {
    this.finished = false
    this.active = this.initial || this.buttons[0]
    this.buttons.forEach((button) => {
      button.disabled = false
    })
    this.element.querySelectorAll("form").forEach((form) => form.reset())
    this.setup()
    this.emit("reset")
  }

  handleClick(event) {
    const action = event.target.closest("[data-coreui-stepper-action]")?.dataset.coreuiStepperAction
    if (action) {
      event.preventDefault()
      this[action]?.()
      return
    }
    const button = event.target.closest(".stepper-step-button")
    if (button && this.element.contains(button)) this.showStep(button)
  }

  handleKeydown(event) {
    if (!event.target.matches(".stepper-step-button")) return
    const index = this.buttons.indexOf(event.target)
    if (["ArrowRight", "ArrowDown"].includes(event.key)) this.buttons[(index + 1) % this.buttons.length]?.focus()
    if (["ArrowLeft", "ArrowUp"].includes(event.key))
      this.buttons[(index - 1 + this.buttons.length) % this.buttons.length]?.focus()
    if (event.key === "Home") this.buttons[0]?.focus()
    if (event.key === "End") this.buttons.at(-1)?.focus()
  }
  snapshotState() {
    return {
      step: this.buttons.indexOf(this.active),
      initial: this.buttons.indexOf(this.initial),
      finished: Boolean(this.finished),
    }
  }

  restoreState(state) {
    if (state.step === undefined) return
    this.active = this.buttons[state.step] || this.buttons[0]
    this.initial = this.buttons[state.initial] || this.buttons[0]
    this.setup()
    this.finished = state.finished
    if (this.finished) {
      this.active.classList.remove("active")
      this.active.classList.add("complete")
      this.buttons.forEach((button) => {
        button.disabled = true
      })
      this.pane(this.active)?.classList.remove("active", "show")
    }
  }
}
